import { useState, type ReactNode } from "react";

import {
  returnReasonMessage,
  type CollectionMethod,
  type LineReasonCode,
  type LookupResponse,
  type PolicyViolation,
} from "@sirdab/returns-shared";
import { Alert, AlertDescription, AlertTitle } from "@sirdab/ui";

import { useApi, type ApiFailure } from "../api";
import {
  ChoiceGroup,
  ChoiceOption,
  ErrorState,
  FeeDisclosure,
  LineItemRow,
  StepActions,
} from "../components";
import { useFlow } from "../flow";
import { useMessages } from "../i18n";
import { buildPath, navigate } from "../routes";
import {
  feeTotalHalalas,
  OfflineNote,
  RequireLookup,
  useFailureText,
} from "./shared";
import { StepScaffold } from "./StepScaffold";
import type { TrackHandoff } from "./TrackScreen";

function Section({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <section className="border-border flex flex-col gap-2 border-b pb-3 last:border-b-0">
      <h2 className="text-caption text-subtle uppercase">{heading}</h2>
      {children}
    </section>
  );
}

/**
 * Step 6 of docs/08 — everything restated, then submit.
 *
 * The fee appears here for the second and last time before submission (policy check 9),
 * so a deduction can never be a surprise. Drop-off is offered only when `dropoffAllowed`
 * (policy check 10); when it is not, pickup is stated as the only option rather than
 * being the sole radio with no explanation.
 *
 * A `422 policy_violation` is not an error page: the server re-validates everything
 * (docs/06) and each violation carries its own bilingual text, which is rendered in full
 * and next to the line it concerns.
 *
 * A successful submit does not stop at a confirmation page (SAI-1324). The draft is wiped
 * from memory and the shopper lands on tracking for the return she just made, with the
 * reference and fee carried on the history entry so no second form has to be filled in.
 */
function Body({ lookup }: { lookup: LookupResponse }): React.JSX.Element {
  const api = useApi();
  const { t, text, locale } = useMessages();
  const failureText = useFailureText();
  const {
    merchantSlug,
    orderRef,
    phone,
    lines: drafts,
    resolution,
    collectionMethod,
    shopperToken,
    update,
    reset,
    back,
  } = useFlow();

  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [violations, setViolations] = useState<PolicyViolation[]>([]);

  const fee = feeTotalHalalas(lookup.policy, drafts, lookup.order.lines);
  const methods: CollectionMethod[] = lookup.policy.dropoffAllowed
    ? ["pickup", "dropoff"]
    : ["pickup"];

  // A line's reason resolves through the catalogue, never through `policy.reasons[]`.
  // That list holds only the codes a shopper may PICK; a line opened by a warehouse sweep
  // carries a system-originated code (`cod_refused`) that is not in it, and looking it up
  // there yields a blank. `returnReasonMessage` covers both vocabularies.
  const reasonLabel = (code: LineReasonCode | null): string =>
    code === null ? "" : text(returnReasonMessage(code));

  const ready =
    drafts.length > 0 &&
    drafts.every((draft) => draft.reasonCode !== null) &&
    resolution !== null &&
    collectionMethod !== null;

  async function submit(): Promise<void> {
    if (!ready || resolution === null || collectionMethod === null) return;
    setFailure(null);
    setViolations([]);
    setBusy(true);

    const result = await api.createReturn(
      {
        merchantSlug,
        orderRef,
        phone,
        // `ready` has already established every draft carries a reason; the flatMap keeps
        // that a type-level fact rather than an assertion.
        lines: drafts.flatMap((draft) =>
          draft.reasonCode === null
            ? []
            : [
                {
                  lineRef: draft.lineRef,
                  qty: draft.qty,
                  reasonCode: draft.reasonCode,
                  ...(draft.subReason === null
                    ? {}
                    : { subReason: draft.subReason }),
                  ...(draft.note === null || draft.note.trim() === ""
                    ? {}
                    : { note: draft.note.trim() }),
                },
              ],
        ),
        resolution,
        collectionMethod,
        evidence: drafts.flatMap((draft) =>
          draft.uploadIds.map((uploadId) => ({
            lineRef: draft.lineRef,
            uploadId,
          })),
        ),
        locale,
      },
      shopperToken ?? undefined,
    );
    setBusy(false);

    if (!result.ok) {
      if (result.kind === "response" && result.violations.length > 0) {
        setViolations(result.violations);
      }
      setFailure(result);
      return;
    }

    const handoff: TrackHandoff = {
      reference: result.data.reference,
      phone,
      feeApplied: result.data.feeApplied,
    };
    // Order contents leave memory before the route changes; nothing was ever stored.
    reset();
    navigate(buildPath("track", merchantSlug), handoff);
  }

  return (
    <div className="flex flex-col gap-4">
      <OfflineNote />

      <Section heading={t("review.items_heading")}>
        <ul className="flex flex-col">
          {drafts.map((draft) => {
            const line = lookup.order.lines.find(
              (entry) => entry.lineRef === draft.lineRef,
            );
            if (line === undefined) return null;
            return (
              <LineItemRow
                key={draft.lineRef}
                line={{ ...line, qty: draft.qty }}
                aside={
                  <span className="text-caption-quiet text-muted-foreground">
                    {reasonLabel(draft.reasonCode)}
                  </span>
                }
              >
                {draft.note !== null && draft.note.trim() !== "" ? (
                  <p className="text-caption-quiet text-muted-foreground">
                    {draft.note}
                  </p>
                ) : null}
                <p className="text-caption-quiet text-subtle">
                  {draft.uploadIds.length > 0
                    ? `${t("review.photos_attached")}: ${draft.uploadIds.length}`
                    : t("evidence.no_photos")}
                </p>
              </LineItemRow>
            );
          })}
        </ul>
      </Section>

      <Section heading={t("review.resolution_heading")}>
        <p className="text-body text-foreground">
          {resolution === null
            ? t("validation.resolution_required")
            : t(`resolution.${resolution}`)}
        </p>
        <p className="text-caption-quiet text-muted-foreground">
          {t("portal.resolution_is_a_request")}
        </p>
      </Section>

      <Section heading={t("review.collection_heading")}>
        <p className="text-caption-quiet text-muted-foreground">
          {t("review.collection_hint")}
        </p>
        <ChoiceGroup
          value={collectionMethod}
          onChange={(method) =>
            update({ collectionMethod: method as CollectionMethod })
          }
        >
          {methods.map((method) => (
            <ChoiceOption
              key={method}
              value={method}
              checked={collectionMethod === method}
              label={t(`collection.${method}`)}
            />
          ))}
        </ChoiceGroup>
        {!lookup.policy.dropoffAllowed ? (
          <p className="text-caption-quiet text-muted-foreground">
            {t("portal.pickup_only")}
          </p>
        ) : null}
      </Section>

      <Section heading={t("review.fee_heading")}>
        {/* Disclosure point two of two. Same figure, stated again before submit. */}
        <FeeDisclosure totalHalalas={fee} point="review" />
      </Section>

      {violations.length > 0 ? (
        <Alert variant="destructive">
          <AlertTitle>{t("error.policy_violation")}</AlertTitle>
          <AlertDescription>
            <ul className="flex list-disc flex-col gap-1 ps-4">
              {violations.map((violation, index) => (
                <li key={`${violation.code}-${index}`}>
                  {text(violation.reasonText)}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : failure !== null ? (
        <ErrorState
          message={failureText(failure)}
          onRetry={() => void submit()}
        />
      ) : null}

      <StepActions
        onNext={() => void submit()}
        onBack={back}
        nextLabel={t("action.submit")}
        nextDisabled={!ready}
        nextLoading={busy}
      />
    </div>
  );
}

export function ReviewScreen(): React.JSX.Element {
  return (
    <StepScaffold
      step="review"
      titleKey="screen.review.title"
      descriptionKey="screen.review.description"
    >
      <RequireLookup>{(lookup) => <Body lookup={lookup} />}</RequireLookup>
    </StepScaffold>
  );
}
